import { useState, useEffect, useRef } from "react";
import { Form, OverlayTrigger, Tooltip, DropdownButton, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDrag, useDrop } from "react-dnd";
import {
    Exercise, ExerciseType, QuestionType, QUESTION_TYPES,
    ClozePayload, ClozeGapSubtype, CLOZE_GAP_SUBTYPES, DEFAULT_GRADE, DEFAULT_PENALTY
} from "../../interfaces/Exercise";
import { getExercise, createExercise, updateExercise } from "../../../services/exercises";
import { showSuccessNotification, showErrorNotification } from "../../../utils/api";
import { exerciseToMoodleXml, moodleXmlFilename } from "../../../utils/moodleExport";

/**
 * EditExercise — dissenyador d'exercicis (Fase 6). Esquelet inicial.
 * Veure taskgen-srv/docs/Disseny-DissenyadorExercicis.md i interfaces/Exercise.
 *
 * Estat actual: editor de preguntes independents (els 6 subtipus, formularis
 * bàsics) + persistència via /v1/exercise. El Cloze i el multimèdia/rich-text a
 * l'enunciat queden per a la següent iteració.
 *
 * Es renderitza incrustat dins el modal de UnifiedTaskEditor (com EditInteraction):
 * porta els seus propis botons Desa/Cancel·la; el pare no posa footer.
 */
export interface EditExerciseProps {
    task_id: string;
    task_language?: string;
    exerciseId?: string | null;     // si existeix, s'edita; si no, se'n crea un
    embedded?: boolean;
    onSaved: (exerciseId: string) => void;   // retorna l'_id per desar-lo al task
    onClose: () => void;                      // tornar al formulari de la tasca
}

let idCounter = 0;
const genId = () => 'q' + (idCounter++) + '-' + Math.random().toString(36).slice(2, 7);

// Icona d'ajuda amb tooltip (Moodle: default grade / penalty no són evidents).
const helpIcon = (text: string) => (
    <OverlayTrigger placement="top" overlay={ <Tooltip>{ text }</Tooltip> }>
        <span className="exercise-help" tabIndex={ 0 }>ⓘ</span>
    </OverlayTrigger>
);

const newQuestion = (qtype: QuestionType): any => {
    const base = { id: genId(), type: qtype, statement: '' };
    switch (qtype) {
        case 'shortanswer': return { ...base, answers: [{ text: '', weight: 1 }], caseSensitive: false };
        case 'truefalse':   return { ...base, correct: true };
        case 'multichoice': return { ...base, multiple: false, options: [{ text: '', correct: true }, { text: '', correct: false }] };
        case 'matching':    return { ...base, pairs: [{ left: '', right: '' }, { left: '', right: '' }] };
        case 'ordering':    return { ...base, items: ['', ''] };
        case 'essay':
        default:            return base;
    }
};

export default function EditExercise(props: EditExerciseProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [type, setType] = useState<ExerciseType>('questions');
    const [questions, setQuestions] = useState<any[]>([]);
    const [cloze, setCloze] = useState<ClozePayload>({ text: '', gaps: [] });
    const [defaultgrade, setDefaultgrade] = useState<number>(DEFAULT_GRADE);
    const [penalty, setPenalty] = useState<number>(DEFAULT_PENALTY);
    const [saving, setSaving] = useState(false);
    const [addType, setAddType] = useState<QuestionType>('multichoice');
    // Plegat/desplegat per pregunta (per `id`). Absent = plegada.
    const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (!props.exerciseId) return;
        getExercise(props.exerciseId, navigate).then(res => {
            const ex = res.data;
            if (!ex) return;
            setName(ex.name || '');
            setType(ex.type || 'questions');
            if (ex.type === 'cloze') setCloze((ex.payload as ClozePayload) || { text: '', gaps: [] });
            else setQuestions(((ex.payload as any)?.questions) || []);
            setDefaultgrade(ex.defaultgrade ?? DEFAULT_GRADE);
            setPenalty(ex.penalty ?? DEFAULT_PENALTY);
        });
    }, []);

    // ── Questions ──
    const addQuestion = () => {
        const q = newQuestion(addType);
        setQuestions(prev => [...prev, q]);
        setExpandedKeys(prev => ({ ...prev, [q.id]: true }));   // desplegada en afegir
    };
    const removeQuestion = (index: number) => setQuestions(prev => prev.filter((_, i) => i !== index));
    const updateQuestion = (index: number, patch: any) =>
        setQuestions(prev => prev.map((q, i) => i === index ? { ...q, ...patch } : q));
    const toggleExpand = (id: string) => setExpandedKeys(prev => ({ ...prev, [id]: !prev[id] }));
    const moveQuestion = (from: number, to: number) => {
        if (from === to || from < 0 || to < 0) return;
        setQuestions(prev => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    const buildExercise = (): Exercise => ({
        name, type, payload: type === 'cloze' ? cloze : { questions }, defaultgrade, penalty,
    });

    // ── Export ──
    const handleExportMoodleXml = () => {
        const exercise = buildExercise();
        const xml = exerciseToMoodleXml(exercise);
        const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = moodleXmlFilename(exercise);
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    // ── Save ──
    const handleSave = () => {
        const exercise = buildExercise();
        setSaving(true);
        const req = props.exerciseId
            ? updateExercise(props.exerciseId, exercise, navigate)
            : createExercise(exercise, navigate);
        req.then(res => {
            setSaving(false);
            if (!res.data) return;   // error ja notificat pel wrapper
            showSuccessNotification(t("Exercise saved"));
            props.onSaved(res.data._id as string);
            props.onClose();
        });
    };

    const labelForType = (qt: QuestionType) => t('exercise_qtype_' + qt);

    return (
        <div className="exercise-designer">
            <Form className="exercise-form" onSubmit={ (e: any) => e.preventDefault() }>
                <Form.Group className="field field-title">
                    <Form.Label>{ t("Exercise name") }</Form.Label>
                    <Form.Control type="text" value={ name } onChange={ e => setName(e.target.value) } />
                </Form.Group>

                <div className="exercise-grade-row">
                    <Form.Group className="field field-exercise-type">
                        <Form.Label>{ t("Exercise type") }</Form.Label>
                        <Form.Select value={ type } onChange={ e => setType(e.target.value as ExerciseType) }>
                            <option value="questions">{ t("exercise_type_questions") }</option>
                            <option value="cloze">{ t("exercise_type_cloze") }</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="field">
                        <Form.Label>{ t("Default grade") } { helpIcon(t("Default grade help")) }</Form.Label>
                        <Form.Control type="number" step="0.1" value={ defaultgrade }
                            onChange={ e => setDefaultgrade(parseFloat(e.target.value)) } />
                    </Form.Group>
                    <Form.Group className="field">
                        <Form.Label>{ t("Penalty") } { helpIcon(t("Penalty help")) }</Form.Label>
                        <Form.Control type="number" step="0.0001" value={ penalty }
                            onChange={ e => setPenalty(parseFloat(e.target.value)) } />
                    </Form.Group>
                </div>

                { type === 'cloze' &&
                    <ClozeEditor value={ cloze } onChange={ setCloze } />
                }

                { type === 'questions' &&
                    <div className="questions-editor">
                        <div className="questions-add">
                            <Form.Select value={ addType } onChange={ e => setAddType(e.target.value as QuestionType) }>
                                { QUESTION_TYPES.map(qt => (
                                    <option key={ qt } value={ qt }>{ labelForType(qt) }</option>
                                )) }
                            </Form.Select>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={ addQuestion }>
                                + { t("Add question") }
                            </button>
                        </div>

                        { questions.length === 0 &&
                            <div className="questions-empty text-muted">{ t("No questions yet.") }</div>
                        }

                        { questions.map((q, index) => (
                            <QuestionRow
                                key={ q.id || index }
                                q={ q }
                                index={ index }
                                expanded={ !!expandedKeys[q.id] }
                                moveQuestion={ moveQuestion }
                                onToggleExpand={ () => toggleExpand(q.id) }
                                onUpdate={ updateQuestion }
                                onRemove={ () => removeQuestion(index) }
                            />
                        )) }
                    </div>
                }

                <div className="exercise-actions">
                    <DropdownButton
                        title={ <><i className="bi bi-download me-1" />{ t("Export") }</> }
                        variant="primary" size="sm" className="exercise-export">
                        <Dropdown.Item onClick={ handleExportMoodleXml }>{ t("Moodle (XML)") }</Dropdown.Item>
                    </DropdownButton>
                    <button type="button" className="btn btn-secondary" onClick={ props.onClose }>{ t("Cancel") }</button>
                    <button type="button" className="btn btn-primary" disabled={ saving } onClick={ handleSave }>
                        { saving ? t("Saving...") : t("Save") }
                    </button>
                </div>
            </Form>
        </div>
    );
}

interface QuestionRowProps {
    q: any;
    index: number;
    expanded: boolean;
    moveQuestion: (from: number, to: number) => void;
    onToggleExpand: () => void;
    onUpdate: (index: number, patch: any) => void;
    onRemove: () => void;
}

/**
 * QuestionRow — una targeta de pregunta: plegable (capçalera amb chevron) i
 * arrossegable per reordenar (react-dnd, mateix patró que MaterialRow). Depèn
 * d'un DndProvider ancestre (el munta UnifiedTaskEditor). Feedback visual:
 * .dragging (origen atenuat) + .drop-before/.drop-after (línia d'inserció).
 */
function QuestionRow(props: QuestionRowProps) {
    const { t } = useTranslation();
    const { q, index, expanded, moveQuestion } = props;

    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: 'exercise-question',
        item: { index },
        collect: monitor => ({ isDragging: monitor.isDragging() }),
    }), [index]);

    const [{ isOver, draggedIndex }, drop] = useDrop(() => ({
        accept: 'exercise-question',
        drop: (item: { index: number }) => { if (item.index !== index) moveQuestion(item.index, index); },
        collect: monitor => ({
            isOver: monitor.isOver(),
            draggedIndex: (monitor.getItem() as { index: number } | null)?.index ?? null,
        }),
    }), [index, moveQuestion]);

    const showLine = isOver && draggedIndex !== null && draggedIndex !== index;
    const dropBefore = showLine && (draggedIndex as number) > index;
    const dropAfter = showLine && (draggedIndex as number) < index;

    const className = 'question-card'
        + (expanded ? ' expanded' : ' collapsed')
        + (isDragging ? ' dragging' : '')
        + (dropBefore ? ' drop-before' : '')
        + (dropAfter ? ' drop-after' : '');

    const summary = (q.statement || '').replace(/<[^>]*>/g, '').trim();

    return (
        <div className={ className } ref={ (node: HTMLDivElement | null) => { drop(node); preview(node); } }>
            <div className="question-card-header">
                <span className="question-drag" ref={ drag as any } title={ t("Drag to reorder") }>⠿</span>
                <a className="question-toggle" onClick={ props.onToggleExpand }>
                    <span className="question-chevron" />
                    <span className="question-type-badge">{ t('exercise_qtype_' + q.type) }</span>
                    { summary &&
                        <span className="question-summary">{ summary.length > 60 ? summary.slice(0, 60) + '…' : summary }</span>
                    }
                </a>
                <button type="button" className="btn btn-link delete" onClick={ props.onRemove }>✕</button>
            </div>
            { expanded &&
                <div className="question-body">
                    <Form.Group className="field">
                        <Form.Label>{ t("Statement") }</Form.Label>
                        <Form.Control as="textarea" rows={ 2 } value={ q.statement || '' }
                            onChange={ e => props.onUpdate(index, { statement: e.target.value }) } />
                    </Form.Group>
                    { renderQuestionBody(q, index, props.onUpdate, t) }

                    { /* Override opcional de grade/penalty per a aquesta pregunta;
                         buit = s'usa el valor per defecte de l'exercici. */ }
                    <div className="question-grade-row">
                        <Form.Group className="field">
                            <Form.Label>{ t("Default grade") } { helpIcon(t("Question grade override help")) }</Form.Label>
                            <Form.Control type="number" step="0.1" value={ q.defaultgrade ?? '' }
                                placeholder={ String(DEFAULT_GRADE) }
                                onChange={ e => props.onUpdate(index, { defaultgrade: e.target.value === '' ? undefined : parseFloat(e.target.value) }) } />
                        </Form.Group>
                        <Form.Group className="field">
                            <Form.Label>{ t("Penalty") } { helpIcon(t("Question penalty override help")) }</Form.Label>
                            <Form.Control type="number" step="0.0001" value={ q.penalty ?? '' }
                                placeholder={ String(DEFAULT_PENALTY) }
                                onChange={ e => props.onUpdate(index, { penalty: e.target.value === '' ? undefined : parseFloat(e.target.value) }) } />
                        </Form.Group>
                    </div>
                </div>
            }
        </div>
    );
}

/**
 * Cos específic per subtipus de pregunta independent. Formularis bàsics
 * (skeleton); el rich-text + multimèdia a l'enunciat venen després.
 */
function renderQuestionBody(q: any, index: number, updateQuestion: (i: number, patch: any) => void, t: any) {
    switch (q.type) {
        case 'essay':
            return <div className="text-muted">{ t("Open answer — no auto-correction.") }</div>;

        case 'truefalse':
            return (
                <div className="field truefalse-field">
                    <Form.Check inline type="radio" label={ t("True") } checked={ q.correct === true }
                        onChange={ () => updateQuestion(index, { correct: true }) } />
                    <Form.Check inline type="radio" label={ t("False") } checked={ q.correct === false }
                        onChange={ () => updateQuestion(index, { correct: false }) } />
                </div>
            );

        case 'shortanswer': {
            const answers = q.answers || [];
            const setAnswers = (a: any[]) => updateQuestion(index, { answers: a });
            return (
                <div className="field answers-field">
                    <Form.Label>{ t("Accepted answers") }</Form.Label>
                    { answers.map((a: any, ai: number) => (
                        <div className="answer-row" key={ ai }>
                            <Form.Control type="text" placeholder={ t("Answer") } value={ a.text }
                                onChange={ e => setAnswers(answers.map((x: any, i: number) => i === ai ? { ...x, text: e.target.value } : x)) } />
                            <Form.Control type="number" step="0.1" className="weight" value={ a.weight }
                                onChange={ e => setAnswers(answers.map((x: any, i: number) => i === ai ? { ...x, weight: parseFloat(e.target.value) } : x)) } />
                            <button type="button" className="btn btn-link delete" onClick={ () => setAnswers(answers.filter((_: any, i: number) => i !== ai)) }>✕</button>
                        </div>
                    )) }
                    <button type="button" className="btn btn-link add" onClick={ () => setAnswers([...answers, { text: '', weight: 1 }]) }>+ { t("Add answer") }</button>
                    <Form.Check type="checkbox" label={ t("Case sensitive") } checked={ !!q.caseSensitive }
                        onChange={ e => updateQuestion(index, { caseSensitive: e.target.checked }) } />
                </div>
            );
        }

        case 'multichoice': {
            const options = q.options || [];
            const setOptions = (o: any[]) => updateQuestion(index, { options: o });
            return (
                <div className="field options-field">
                    <Form.Check type="checkbox" label={ t("Allow multiple correct answers") } checked={ !!q.multiple }
                        onChange={ e => updateQuestion(index, { multiple: e.target.checked }) } />
                    { options.map((o: any, oi: number) => (
                        <div className="option-row" key={ oi }>
                            <Form.Check type={ q.multiple ? 'checkbox' : 'radio' } checked={ !!o.correct }
                                onChange={ () => setOptions(options.map((x: any, i: number) =>
                                    q.multiple
                                        ? (i === oi ? { ...x, correct: !x.correct } : x)
                                        : { ...x, correct: i === oi })) } />
                            <Form.Control type="text" placeholder={ t("Option") } value={ o.text }
                                onChange={ e => setOptions(options.map((x: any, i: number) => i === oi ? { ...x, text: e.target.value } : x)) } />
                            <button type="button" className="btn btn-link delete" onClick={ () => setOptions(options.filter((_: any, i: number) => i !== oi)) }>✕</button>
                        </div>
                    )) }
                    <button type="button" className="btn btn-link add" onClick={ () => setOptions([...options, { text: '', correct: false }]) }>+ { t("Add option") }</button>
                </div>
            );
        }

        case 'matching': {
            const pairs = q.pairs || [];
            const setPairs = (p: any[]) => updateQuestion(index, { pairs: p });
            return (
                <div className="field pairs-field">
                    <Form.Label>{ t("Matching pairs") }</Form.Label>
                    { pairs.map((p: any, pi: number) => (
                        <div className="pair-row" key={ pi }>
                            <Form.Control type="text" placeholder={ t("Left") } value={ p.left }
                                onChange={ e => setPairs(pairs.map((x: any, i: number) => i === pi ? { ...x, left: e.target.value } : x)) } />
                            <Form.Control type="text" placeholder={ t("Right") } value={ p.right }
                                onChange={ e => setPairs(pairs.map((x: any, i: number) => i === pi ? { ...x, right: e.target.value } : x)) } />
                            <button type="button" className="btn btn-link delete" onClick={ () => setPairs(pairs.filter((_: any, i: number) => i !== pi)) }>✕</button>
                        </div>
                    )) }
                    <button type="button" className="btn btn-link add" onClick={ () => setPairs([...pairs, { left: '', right: '' }]) }>+ { t("Add pair") }</button>
                </div>
            );
        }

        case 'ordering': {
            const items = q.items || [];
            const setItems = (it: any[]) => updateQuestion(index, { items: it });
            return (
                <div className="field items-field">
                    <Form.Label>{ t("Items in correct order") }</Form.Label>
                    { items.map((it: string, ii: number) => (
                        <div className="item-row" key={ ii }>
                            <span className="item-num">{ ii + 1 }</span>
                            <Form.Control type="text" value={ it }
                                onChange={ e => setItems(items.map((x: string, i: number) => i === ii ? e.target.value : x)) } />
                            <button type="button" className="btn btn-link delete" onClick={ () => setItems(items.filter((_: string, i: number) => i !== ii)) }>✕</button>
                        </div>
                    )) }
                    <button type="button" className="btn btn-link add" onClick={ () => setItems([...items, '']) }>+ { t("Add item") }</button>
                </div>
            );
        }

        default:
            return null;
    }
}

// ───────────────────────── Cloze ─────────────────────────
//
// Editor Cloze (opció A): textarea + botó "Fes buit". En seleccionar una
// paraula i triar un subtipus, s'insereix un marcador [[N]] al text i s'afegeix
// el buit a `gaps`. Cada buit es configura en una targeta a sota; hi ha una
// previsualització que mostra els buits com a espais. L'export a Moodle Cloze
// es genera des d'aquest model (no de l'edició).

const gapPreviewLabel = (subtype: ClozeGapSubtype | undefined) => {
    if (subtype === 'multichoice') return '▾';
    if (subtype === 'truefalse') return 'V / F';
    return '______';   // gapfill o desconegut
};

function ClozeEditor(props: { value: ClozePayload; onChange: (c: ClozePayload) => void }) {
    const { t } = useTranslation();
    const textRef = useRef<HTMLTextAreaElement>(null);
    const [addSubtype, setAddSubtype] = useState<ClozeGapSubtype>('gapfill');

    const text = props.value.text || '';
    const gaps: any[] = props.value.gaps || [];

    const nextId = () => String(gaps.reduce((m, g) => Math.max(m, parseInt(g.id) || 0), 0) + 1);

    const setText = (newText: string) => props.onChange({ ...props.value, text: newText });
    const updateGap = (id: string, patch: any) =>
        props.onChange({ ...props.value, gaps: gaps.map(g => g.id === id ? { ...g, ...patch } : g) });
    const removeGap = (id: string) => props.onChange({
        text: text.split('[[' + id + ']]').join(''),
        gaps: gaps.filter(g => g.id !== id),
    });

    const changeGapSubtype = (id: string, subtype: ClozeGapSubtype) => {
        const init: any = subtype === 'gapfill'
            ? { subtype, answer: '', alternatives: [], caseSensitive: false }
            : subtype === 'multichoice'
                ? { subtype, options: [{ text: '', correct: true }, { text: '', correct: false }] }
                : { subtype, correct: true };
        updateGap(id, init);
    };

    const makeGap = () => {
        const ta = textRef.current;
        if (!ta) return;
        const start = ta.selectionStart, end = ta.selectionEnd;
        if (start === end) { showErrorNotification(t("Select a word in the text first.")); return; }
        const selected = text.slice(start, end);
        const id = nextId();
        const newText = text.slice(0, start) + '[[' + id + ']]' + text.slice(end);
        const gap: any = { id, subtype: addSubtype, clue: '' };
        if (addSubtype === 'gapfill') { gap.answer = selected; gap.alternatives = []; gap.caseSensitive = false; }
        else if (addSubtype === 'multichoice') { gap.options = [{ text: selected, correct: true }, { text: '', correct: false }]; }
        else { gap.correct = true; }
        props.onChange({ text: newText, gaps: [...gaps, gap] });
    };

    // Previsualització: parteix el text pels marcadors [[N]] i pinta cada buit.
    const renderPreview = () => {
        const out: any[] = [];
        const regex = /\[\[(\d+)\]\]/g;
        let last = 0, m: RegExpExecArray | null, key = 0;
        while ((m = regex.exec(text)) !== null) {
            if (m.index > last) out.push(<span key={ key++ }>{ text.slice(last, m.index) }</span>);
            const gap = gaps.find(g => g.id === m![1]);
            out.push(
                <span key={ key++ } className="cloze-blank">
                    { gapPreviewLabel(gap?.subtype) }<sup>{ m[1] }</sup>
                </span>
            );
            last = m.index + m[0].length;
        }
        if (last < text.length) out.push(<span key={ key++ }>{ text.slice(last) }</span>);
        return out;
    };

    return (
        <div className="cloze-editor">
            <Form.Group className="field">
                <Form.Label>{ t("Cloze text") }</Form.Label>
                <Form.Control as="textarea" rows={ 4 } ref={ textRef as any } value={ text }
                    onChange={ e => setText(e.target.value) } />
            </Form.Group>

            <div className="cloze-make">
                <span className="hint">{ t("Select a word and turn it into a gap:") }</span>
                <Form.Select value={ addSubtype } onChange={ e => setAddSubtype(e.target.value as ClozeGapSubtype) }>
                    { CLOZE_GAP_SUBTYPES.map(s => (
                        <option key={ s } value={ s }>{ t('cloze_gap_' + s) }</option>
                    )) }
                </Form.Select>
                <button type="button" className="btn btn-secondary btn-sm" onClick={ makeGap }>+ { t("Make gap") }</button>
            </div>

            { gaps.length > 0 &&
                <div className="cloze-preview">
                    <div className="label">{ t("Preview") }</div>
                    <div className="cloze-preview-text">{ renderPreview() }</div>
                </div>
            }

            <div className="cloze-gaps">
                { gaps.map(g => (
                    <div className="cloze-gap-card" key={ g.id }>
                        <div className="cloze-gap-header">
                            <span className="cloze-gap-num">{ t("Gap") } { g.id }</span>
                            <Form.Select size="sm" value={ g.subtype }
                                onChange={ e => changeGapSubtype(g.id, e.target.value as ClozeGapSubtype) }>
                                { CLOZE_GAP_SUBTYPES.map(s => (
                                    <option key={ s } value={ s }>{ t('cloze_gap_' + s) }</option>
                                )) }
                            </Form.Select>
                            <button type="button" className="btn btn-link delete" onClick={ () => removeGap(g.id) }>✕</button>
                        </div>
                        { renderGapBody(g, updateGap, t) }
                        <Form.Group className="field clue-field">
                            <Form.Label>{ t("Clue") }</Form.Label>
                            <Form.Control type="text" value={ g.clue || '' }
                                onChange={ e => updateGap(g.id, { clue: e.target.value }) } />
                        </Form.Group>
                    </div>
                )) }
            </div>
        </div>
    );
}

/** Cos de configuració d'un buit Cloze segons el subtipus. */
function renderGapBody(g: any, updateGap: (id: string, patch: any) => void, t: any) {
    switch (g.subtype) {
        case 'truefalse':
            return (
                <div className="field truefalse-field">
                    <Form.Check inline type="radio" label={ t("True") } checked={ g.correct === true }
                        onChange={ () => updateGap(g.id, { correct: true }) } />
                    <Form.Check inline type="radio" label={ t("False") } checked={ g.correct === false }
                        onChange={ () => updateGap(g.id, { correct: false }) } />
                </div>
            );

        case 'multichoice': {
            const options = g.options || [];
            const setOptions = (o: any[]) => updateGap(g.id, { options: o });
            return (
                <div className="field options-field">
                    { options.map((o: any, oi: number) => (
                        <div className="option-row" key={ oi }>
                            <Form.Check type="radio" checked={ !!o.correct }
                                onChange={ () => setOptions(options.map((x: any, i: number) => ({ ...x, correct: i === oi }))) } />
                            <Form.Control type="text" placeholder={ t("Option") } value={ o.text }
                                onChange={ e => setOptions(options.map((x: any, i: number) => i === oi ? { ...x, text: e.target.value } : x)) } />
                            <button type="button" className="btn btn-link delete" onClick={ () => setOptions(options.filter((_: any, i: number) => i !== oi)) }>✕</button>
                        </div>
                    )) }
                    <button type="button" className="btn btn-link add" onClick={ () => setOptions([...options, { text: '', correct: false }]) }>+ { t("Add option") }</button>
                </div>
            );
        }

        case 'gapfill':
        default: {
            const alternatives = g.alternatives || [];
            const setAlts = (a: string[]) => updateGap(g.id, { alternatives: a });
            return (
                <div className="field gapfill-field">
                    <Form.Group className="field">
                        <Form.Label>{ t("Correct answer") }</Form.Label>
                        <Form.Control type="text" value={ g.answer || '' }
                            onChange={ e => updateGap(g.id, { answer: e.target.value }) } />
                    </Form.Group>
                    <Form.Label>{ t("Alternatives") }</Form.Label>
                    { alternatives.map((a: string, ai: number) => (
                        <div className="answer-row" key={ ai }>
                            <Form.Control type="text" value={ a }
                                onChange={ e => setAlts(alternatives.map((x: string, i: number) => i === ai ? e.target.value : x)) } />
                            <button type="button" className="btn btn-link delete" onClick={ () => setAlts(alternatives.filter((_: string, i: number) => i !== ai)) }>✕</button>
                        </div>
                    )) }
                    <button type="button" className="btn btn-link add" onClick={ () => setAlts([...alternatives, '']) }>+ { t("Add alternative") }</button>
                    <Form.Check type="checkbox" label={ t("Case sensitive") } checked={ !!g.caseSensitive }
                        onChange={ e => updateGap(g.id, { caseSensitive: e.target.checked }) } />
                </div>
            );
        }
    }
}
